import React, { useMemo } from "react";
import {
    Autocomplete,
    TextField,
    Checkbox,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useControlledAutocomplete } from "../hooks";

export type ControlledAutocompleteVariant =
    | "PROJECT"
    | "PORTFOLIO"
    | "PROGRAMME"
    | "SEARCH_PORTFOLIO_PROJECTS";

export interface AutocompleteOption {
    id: string | number;
    label?: string;
    project_reference?: string;
    [key: string]: any;
}

interface ControlledAutocompleteProps {
    type: ControlledAutocompleteVariant;
    value: AutocompleteOption | AutocompleteOption[] | null;
    label?: string;
    placeholder?: string;
    multiSelect?: boolean;
    onSelect: (
        value: AutocompleteOption | AutocompleteOption[] | null,
        type: ControlledAutocompleteVariant
    ) => void;
    disabled?: boolean;
    error?: boolean;
    helperText?: string | null;
    required?: boolean;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ControlledAutocomplete: React.FC<ControlledAutocompleteProps> = ({
    value,
    label = "Search",
    placeholder = "Type to search...",
    type,
    multiSelect = false,
    onSelect,
    disabled = false,
    error = false,
    helperText,
    required = false,
}) => {

    const { options, loading, onChange, onInputChange } = useControlledAutocomplete(type);

    const getDisplayText = (option: AutocompleteOption) => {
        if (type === "PROJECT") return option.title || option.label || "";
        return option.portfolio_name || option.name || option.label || option.description || "";
    };

    const structuredOptions = useMemo(() => {
        return [...options].sort((a, b) =>
            getDisplayText(a).toLowerCase().localeCompare(getDisplayText(b).toLowerCase())
        );
    }, [options, getDisplayText]);

    /** -------------------------------
     * SELECTION HANDLING
     * -------------------------------- */
    const handleSelect = (newValue: any) => {
        onSelect(newValue, type);
    };

    return (
        <Autocomplete
            disabled={disabled}
            multiple={multiSelect}
            disableCloseOnSelect={multiSelect}
            loading={loading}
            value={multiSelect ? (Array.isArray(value) ? value : []) : value}
            options={structuredOptions}
            onChange={(_, newValue) => {
                onChange(
                    type,
                    Array.isArray(newValue) ? newValue[0] ?? null : newValue
                );
                handleSelect(newValue);
            }}
            onInputChange={onInputChange}
            getOptionLabel={(option) => option.label || getDisplayText(option)}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            renderOption={multiSelect ? (props, option, { selected }) => (
                <li {...props}>
                    <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                    {option.label || getDisplayText(option)}
                </li>
            ) : undefined}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    required={required}
                />
            )}
            sx={{ width: "100%" }}
        />
    );
};
